import { useEffect, useRef } from "react";

/** Run `retry` once each time the connection comes back, but only when the
 *  surface was actually waiting on it.
 *
 *  `waiting` is the launch that has not become operable yet (not "offline"):
 *  the hook arms on the offline edge and fires on the next online edge, so a
 *  reconnect outside a launch never starts one. One retry per offline episode:
 *  the next automatic attempt needs a new offline→online transition, which
 *  keeps a retry that fails immediately from looping. */
export function useReconnectRetry(online: boolean, waiting: boolean, retry: () => void) {
  const armed = useRef(false);
  const retryRef = useRef(retry);
  retryRef.current = retry;
  useEffect(() => {
    if (!waiting) { armed.current = false; return; }
    if (!online) { armed.current = true; return; }
    if (!armed.current) return;
    armed.current = false;
    retryRef.current();
  }, [online, waiting]);
}
